import React, { useState, useEffect, useRef } from 'react';
import cogoToast from 'cogo-toast';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';

import { getSupplementalInvoicePreview, createSupplementalInvoice } from '../../api/spotlights';
import { fmtMoney2 } from '../../helpers/spotlights_dashboard_helpers';

// Send a one-off invoice for an already-closed period with free-form line items — e.g. spend the
// scheduled run missed (it bills whatever SpotlightAnalytics said at cut time, and never revisits
// a period). Unlike a hand-made Stripe invoice, this lands in SpotlightInvoices, so the brand sees
// it on their Subscriptions page and the paid/void webhooks track it.
const SupplementalInvoiceModal = ({ brand, onClose, onDone }) => {
  const [periodStart, setPeriodStart] = useState(moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'));
  const [periodEnd, setPeriodEnd] = useState(moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'));
  const [context, setContext] = useState(null);
  const [contextError, setContextError] = useState(null);
  const [rows, setRows] = useState([{ key: 1, label: '', amount: '' }]);
  const rowKeyRef = useRef(1);
  const [feePct, setFeePct] = useState(String(brand.platformFeePercentage ?? ''));
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // A context response only applies if the dates haven't changed while it was in flight.
  const currentPeriodRef = useRef();
  currentPeriodRef.current = `${periodStart}|${periodEnd}`;

  // The fee is seeded from the brand's settings but never re-seeded over an admin's edit —
  // a date change would otherwise silently snap an overridden fee back to the default.
  const feeTouchedRef = useRef(false);

  useEffect(() => {
    if (!periodStart || !periodEnd) {
      setLoading(false);
      setContext(null);
      setContextError(null);
      return undefined;
    }
    const requestedPeriod = `${periodStart}|${periodEnd}`;
    setLoading(true);
    setContext(null);
    setContextError(null);
    // Debounced: keyboard-editing a date input fires one change per segment.
    const timer = setTimeout(() => {
      getSupplementalInvoicePreview(brand.Brand_id, { periodStart, periodEnd })
        .then(r => {
          if (currentPeriodRef.current !== requestedPeriod) return;
          setContext(r);
          if (!feeTouchedRef.current) setFeePct(String(r.platformFeePercentage));
        })
        .catch(e => {
          if (currentPeriodRef.current !== requestedPeriod) return;
          setContextError(typeof e === 'string' ? e : 'Failed to check the period.');
        })
        .finally(() => {
          if (currentPeriodRef.current === requestedPeriod) setLoading(false);
        });
    }, 300);
    return () => clearTimeout(timer);
  }, [brand.Brand_id, periodStart, periodEnd]);

  const setRow = (key, patch) => setRows(rows.map(r => (r.key === key ? { ...r, ...patch } : r)));
  const addRow = () => {
    rowKeyRef.current += 1;
    setRows([...rows, { key: rowKeyRef.current, label: '', amount: '' }]);
  };
  const removeRow = key => setRows(rows.filter(r => r.key !== key));

  const hasInvalidRow = rows.some(r => !r.label.trim() || !(Number(r.amount) > 0));
  const showRowError = hasInvalidRow && rows.some(r => r.label.trim() || r.amount);
  const spendTotal = rows.reduce((sum, r) => sum + (Number(r.amount) > 0 ? Number(r.amount) : 0), 0);
  const feeValid = Number.isFinite(Number(feePct)) && Number(feePct) >= 0 && Number(feePct) <= 100;
  const feeTotal = feeValid ? (spendTotal * Number(feePct)) / 100 : 0;
  const collection = context?.collection;
  const canSend = !!context && collection?.eligible && !loading && rows.length > 0 && !hasInvalidRow && feeValid && !sending;

  const collectionLabel = !collection
    ? null
    : !collection.eligible
    ? collection.reason
    : collection.method === 'charge_automatically'
    ? 'Will auto-charge the card on file.'
    : collection.netDays > 0
    ? `Will send a Stripe invoice due in ${collection.netDays} day(s).`
    : 'Will send a Stripe invoice due immediately.';

  const send = async () => {
    const confirmed = window.confirm(
      `Send a supplemental invoice to ${brand.brandName} for ${fmtMoney2(spendTotal + feeTotal)} (${rows.length} line(s), ${periodStart} – ${periodEnd})?`
    );
    if (!confirmed) return;
    setSending(true);
    try {
      const r = await createSupplementalInvoice(brand.Brand_id, {
        periodStart,
        periodEnd,
        platformFeePercentage: Number(feePct),
        lineItems: rows.map(r => ({ label: r.label.trim(), amount: Number(r.amount) }))
      });
      setResult(r.invoice);
      cogoToast.success(`Supplemental invoice sent to ${brand.brandName}.`);
      onDone && onDone();
    } catch (e) {
      cogoToast.error(typeof e === 'string' ? e : 'Failed to create the invoice.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className='sl-modal-overlay' onMouseDown={onClose}>
      <div className='sl-modal' style={{ maxWidth: 680 }} onMouseDown={e => e.stopPropagation()}>
        <div className='sl-modal-head'>
          <div className='sl-modal-title'>Supplemental invoice — {brand.brandName}</div>
          <button className='sl-modal-close' onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className='sl-modal-body'>
          {result ? (
            <div>
              <div className='sl-card-sub' style={{ marginBottom: 12 }}>
                Invoice #{result.id} created for <strong>{fmtMoney2(result.amount)}</strong> ({fmtMoney2(result.spendAmount)} +{' '}
                {fmtMoney2(result.platformFeeAmount)} fee).
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                {result.stripeHostedUrl && (
                  <a className='sl-btn secondary small' href={result.stripeHostedUrl} target='_blank' rel='noreferrer'>
                    Hosted invoice ↗
                  </a>
                )}
                {result.stripeDashboardUrl && (
                  <a className='sl-btn secondary small' href={result.stripeDashboardUrl} target='_blank' rel='noreferrer'>
                    Stripe dashboard ↗
                  </a>
                )}
                <button className='sl-btn small' onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <label style={{ fontSize: 13 }}>
                  <div className='sl-card-label'>Period start</div>
                  <input type='date' value={periodStart} onChange={e => setPeriodStart(e.target.value)} style={{ padding: 8, borderRadius: 8 }} />
                </label>
                <label style={{ fontSize: 13 }}>
                  <div className='sl-card-label'>Period end</div>
                  <input type='date' value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} style={{ padding: 8, borderRadius: 8 }} />
                </label>
                {loading && <span style={{ fontSize: 13, color: '#767676', paddingBottom: 10 }}>Checking period…</span>}
              </div>

              {contextError && (
                <div className='sl-card-sub' style={{ marginTop: 14, color: '#c0392b' }}>
                  {contextError}
                </div>
              )}

              {context && (
                <>
                  {collection && (
                    <div className='sl-card-sub' style={{ marginTop: 14, color: collection.eligible ? undefined : '#c0392b' }}>
                      {collectionLabel}
                    </div>
                  )}

                  {context.overlappingInvoices?.length > 0 && (
                    <div className='sl-card-sub' style={{ marginTop: 10 }}>
                      Already invoiced for this period:{' '}
                      {context.overlappingInvoices.map(inv => (
                        <span key={inv.id} style={{ marginRight: 8 }}>
                          #{inv.id} {inv.periodStart} – {inv.periodEnd} ({fmtMoney2(inv.spendAmount)} spend, {inv.status}
                          {inv.billingCadence === 'supplemental' ? ', supplemental' : ''}){inv.partialOverlap ? ' ⚠ extends outside this period' : ''}
                        </span>
                      ))}
                    </div>
                  )}

                  <table className='sl-table' style={{ marginTop: 14 }}>
                    <thead>
                      <tr>
                        <th className='no-sort'>Line item</th>
                        <th className='no-sort num'>Amount</th>
                        <th className='no-sort' style={{ width: 40 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(r => (
                        <tr key={r.key}>
                          <td>
                            <input
                              type='text'
                              placeholder='e.g. Ad spend adjustment — July 2026'
                              value={r.label}
                              maxLength={200}
                              onChange={e => setRow(r.key, { label: e.target.value })}
                              style={{ padding: 6, borderRadius: 6, width: '100%' }}
                            />
                          </td>
                          <td className='num'>
                            <input
                              type='number'
                              min='0.01'
                              step='0.01'
                              value={r.amount}
                              onChange={e => setRow(r.key, { amount: e.target.value })}
                              style={{ padding: 6, borderRadius: 6, width: 110, textAlign: 'right' }}
                            />
                          </td>
                          <td>
                            {rows.length > 1 && (
                              <button className='sl-btn secondary small' onClick={() => removeRow(r.key)}>
                                ✕
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div style={{ marginTop: 10 }}>
                    <button className='sl-btn secondary small' onClick={addRow}>
                      Add line
                    </button>
                  </div>

                  <div style={{ display: 'flex', gap: 16, alignItems: 'center', justifyContent: 'flex-end', marginTop: 14, flexWrap: 'wrap' }}>
                    <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
                      Platform fee %
                      <input
                        type='number'
                        min='0'
                        max='100'
                        step='0.1'
                        value={feePct}
                        onChange={e => {
                          feeTouchedRef.current = true;
                          setFeePct(e.target.value);
                        }}
                        style={{ padding: 6, borderRadius: 6, width: 70, textAlign: 'right' }}
                      />
                    </label>
                    {showRowError && <span style={{ fontSize: 13, color: '#c0392b' }}>Every line needs a label and an amount above $0.</span>}
                    <span style={{ fontSize: 13 }}>
                      {fmtMoney2(spendTotal)} + {fmtMoney2(feeTotal)} fee = <strong>{fmtMoney2(spendTotal + feeTotal)}</strong>
                    </span>
                    <button className='sl-btn' disabled={!canSend} onClick={send}>
                      {sending ? 'Sending…' : 'Send invoice'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupplementalInvoiceModal;
